import type { PublicPriceTier } from '@repo/api-contracts';
import { Minus, Plus } from 'lucide-react';
import { type ReactElement, useId } from 'react';
import { parseOrderQty, stepOrderQty } from '@/entities/product';
import { formatCount } from '@/shared/lib';
import { EyebrowLabel, Input } from '@/shared/ui';
import { PriceTierButton } from './price-tier-button';

interface EditorQuantityFieldProps {
  /** 입력창 문자열 그대로 — 직접 입력과 −/+ 가 같은 상태를 고친다. */
  readonly value: string;
  readonly minOrderQty: number;
  /** minQty 오름차순 — 비었으면 수량별 가격 링크를 두지 않는다. */
  readonly tiers: readonly PublicPriceTier[];
  readonly onChange: (value: string) => void;
}

/** 원형 −/+ 버튼 — 상품 상세 수량 선택과 같은 작은 조작부. */
const STEP_BUTTON =
  'inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-foreground/15 bg-background transition-colors hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-foreground/15';

/**
 * 수량 — 상품 상세와 같은 연회색 칸 안의 −/+ 와 숫자 입력, 아래에 수량별 가격 링크.
 * −/+ 는 최소 주문 수량 아래로 내려가지 않고, 직접 입력은 그대로 받아 아래 예상 합계가 검증 결과를 보여 준다.
 */
export const EditorQuantityField = ({
  value,
  minOrderQty,
  tiers,
  onChange,
}: EditorQuantityFieldProps): ReactElement => {
  const inputId = useId();
  const qty = parseOrderQty(value);

  return (
    <div className="space-y-3">
      <EyebrowLabel>Quantity</EyebrowLabel>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 bg-muted/70 px-5 py-4">
        <div className="space-y-0.5">
          <label htmlFor={inputId} className="block text-sm font-semibold">
            수량
          </label>
          <p className="text-xs text-muted-foreground">
            최소 {formatCount(minOrderQty)}개부터 주문할 수 있습니다
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="수량 줄이기"
            // 이미 최소 주문 수량이면 더 줄일 수 없다. 입력이 비었거나 잘못됐으면 눌러서 최소 수량으로 되돌린다.
            disabled={qty !== null && qty <= minOrderQty}
            className={STEP_BUTTON}
            onClick={() => onChange(String(stepOrderQty(qty, minOrderQty, -1)))}
          >
            <Minus aria-hidden className="size-4" strokeWidth={1.5} />
          </button>
          <Input
            id={inputId}
            inputMode="numeric"
            autoComplete="off"
            className="h-9 w-20 bg-background px-2 text-center tabular-nums"
            value={value}
            onChange={(event) => onChange(event.target.value)}
          />
          <button
            type="button"
            aria-label="수량 늘리기"
            className={STEP_BUTTON}
            onClick={() => onChange(String(stepOrderQty(qty, minOrderQty, 1)))}
          >
            <Plus aria-hidden className="size-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
      {tiers.length > 0 && <PriceTierButton tiers={tiers} qty={qty} />}
    </div>
  );
};
